import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "@/lib/api/client";
import { doctorKeys } from "@/lib/api/doctorApi";
import { log } from "@/lib/logger";
import { useQueryClient } from "@/lib/query";
import { videoCallService } from "@/services/video-call/video-call.service";
import { ZegoCallEngine, classifyMediaError } from "@/services/video-call/zego-engine";
import type { CallError, CallSession, CallState } from "@/types/call";

const PROVIDER_UNAVAILABLE = "Video service is currently unavailable. Please try again later.";

function toCallError(error: unknown): CallError {
  if (error instanceof ApiError) {
    if (error.status === 501)
      return { kind: "backend", message: PROVIDER_UNAVAILABLE, retryable: true };
    return { kind: "backend", message: error.message, retryable: error.status !== 403 };
  }
  const kind = classifyMediaError(error);
  if (kind === "permission") {
    return {
      kind: "permission",
      message: "Camera and microphone access is required for a video consultation.",
      retryable: true,
    };
  }
  if (kind === "device") {
    return { kind: "device", message: "No camera or microphone was detected.", retryable: true };
  }
  return {
    kind: "sdk",
    message: error instanceof Error ? error.message : "The video connection failed.",
    retryable: true,
  };
}

const isNoActiveSession = (error: unknown) =>
  error instanceof ApiError &&
  error.status === 404 &&
  /no active call session/i.test(error.message);

export interface VideoCallController {
  state: CallState;
  session: CallSession | null;
  error: CallError | null;
  micMuted: boolean;
  cameraOff: boolean;
  remoteUserPresent: boolean;
  hasRemoteStream: boolean;
  endedDuration: number | null;
  /** True once the patient has been seen in the room during this call. */
  patientAttended: boolean;
  /**
   * This connect is rejoining a call that was already running, not starting one.
   * Drives the wording: the doctor who stepped away has not lost the
   * consultation, and the screen should not imply they have.
   */
  resuming: boolean;
  /**
   * Callback ref for the doctor's self-view CONTAINER — the Zego SDK mounts its
   * own <video> inside it. A callback ref, not a RefObject, so the engine is
   * told the instant the node mounts rather than on the next state change.
   */
  setLocalView: (node: HTMLDivElement | null) => void;
  remoteVideoRef: React.RefObject<HTMLVideoElement | null>;
  /** The local MediaStream being published (for audio recording). */
  localStream: MediaStream | null;
  startCall: () => void;
  retry: () => void;
  toggleMic: () => void;
  toggleCamera: () => void;
  endCall: () => void;
}

/**
 * Owns the whole doctor-side call lifecycle: backend CallSession, ZegoCloud
 * credentials, the SDK engine, and the UI state machine.
 *
 * Contract summary: FRONTEND.MD §9.
 */
export function useVideoCall(bookingId: string): VideoCallController {
  const queryClient = useQueryClient();
  const [state, setState] = useState<CallState>("checking");
  const [session, setSession] = useState<CallSession | null>(null);
  const [error, setError] = useState<CallError | null>(null);
  const [micMuted, setMicMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [remoteUserPresent, setRemoteUserPresent] = useState(false);
  const [hasRemoteStream, setHasRemoteStream] = useState(false);
  const [endedDuration, setEndedDuration] = useState<number | null>(null);
  const [patientAttended, setPatientAttended] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);

  const localViewRef = useRef<HTMLDivElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const engineRef = useRef<ZegoCallEngine | null>(null);
  const connectingRef = useRef(false);
  const mountedRef = useRef(true);
  const stateRef = useRef<CallState>("checking");

  // Refs mirroring state that async SDK callbacks need to read.
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const hasRemoteStreamRef = useRef(false);
  const micMutedRef = useRef(false);
  const cameraOffRef = useRef(false);

  /** Set once the patient is seen; never cleared, so a drop-out still counts as attended. */
  const patientAttendedRef = useRef(false);

  const apply = useCallback((next: CallState) => {
    if (!mountedRef.current) return;
    stateRef.current = next;
    setState(next);
  }, []);

  const markPatientAttended = useCallback(() => {
    if (patientAttendedRef.current) return;
    patientAttendedRef.current = true;
    log.info("call", "patient.attended", { bookingId });
    if (mountedRef.current) setPatientAttended(true);
  }, [bookingId]);

  /**
   * Registers the self-view container with the engine the moment React mounts
   * it. The engine holds the request until the camera exists, so it no longer
   * matters whether the stage mounts before or after `startLocalStream`.
   */
  const setLocalView = useCallback((node: HTMLDivElement | null) => {
    localViewRef.current = node;
    engineRef.current?.attachLocal(node);
  }, []);

  /** Binds whatever remote stream we currently hold to the remote <video>. */
  const bindRemoteVideo = useCallback(() => {
    const el = remoteVideoRef.current;
    if (!el) return;
    const stream = remoteStreamRef.current;
    if (el.srcObject === stream) return;
    el.srcObject = stream;
    if (stream) void el.play().catch(() => undefined);
  }, []);

  const attachRemote = useCallback(
    (stream: MediaStream | null) => {
      remoteStreamRef.current = stream;
      if (stream) markPatientAttended();
      setHasRemoteStream(Boolean(stream));
      bindRemoteVideo();
      if (!mountedRef.current) return;
      if (stream) {
        stateRef.current = "ongoing";
        setState("ongoing");
      } else if (stateRef.current === "ongoing") {
        stateRef.current = "patientLeft";
        setState("patientLeft");
      }
    },
    [bindRemoteVideo, markPatientAttended],
  );

  /**
   * The remote <video> lives in CallStage, which is only rendered once the call
   * is live — so a stream that arrives while the SDK is still connecting has no
   * element to bind to yet. Re-bind after every state change so a freshly
   * mounted stage picks up a stream that is already running. (The self-view
   * needs no equivalent: its callback ref drives the engine directly.)
   */
  useEffect(() => {
    bindRemoteVideo();
  }, [bindRemoteVideo, state, hasRemoteStream]);

  /** Joins the backend-provided room with freshly minted backend credentials. */
  const connectSdk = useCallback(
    async (activeSession: CallSession) => {
      if (connectingRef.current) return;
      connectingRef.current = true;
      apply("connectingSdk");
      try {
        const stale = engineRef.current;
        if (stale) {
          engineRef.current = null;
          log.warn("call", "engine.replacingStale", { bookingId });
          await stale.teardown();
        }

        const credentials = await videoCallService.getCallToken(bookingId);

        const engine = new ZegoCallEngine({
          onRemoteStream: attachRemote,
          onRemoteUserPresent: (present) => {
            if (present) markPatientAttended();
            setRemoteUserPresent(present);
          },
          onRoomState: (roomState) => {
            if (!mountedRef.current) return;
            if (roomState === "DISCONNECTED" && stateRef.current !== "ended") {
              stateRef.current = "reconnecting";
              setState("reconnecting");
            } else if (roomState === "CONNECTED" && stateRef.current === "reconnecting") {
              const next = hasRemoteStreamRef.current ? "ongoing" : "waitingForPatient";
              stateRef.current = next;
              setState(next);
            }
          },
          onPublishFailed: (errorCode) => {
            if (!mountedRef.current || stateRef.current === "ending") return;
            setError({
              kind: "sdk",
              message: `Your camera and microphone stopped reaching the patient (error ${errorCode}). Try rejoining the call.`,
              retryable: true,
            });
            stateRef.current = "error";
            setState("error");
          },
          onTokenWillExpire: () => {
            void (async () => {
              try {
                const fresh = await videoCallService.getCallToken(bookingId);
                await engineRef.current?.renewToken(fresh.token);
              } catch {
                /* the SDK keeps the old token until it truly expires */
              }
            })();
          },
          onLocalStreamReady: (stream) => {
            if (!mountedRef.current) return;
            setLocalStream(stream);
          },
        });
        engineRef.current = engine;
        engine.attachLocal(localViewRef.current);

        await engine.join({
          appId: credentials.appId,
          token: credentials.token,
          roomId: credentials.roomId,
          userId: credentials.userId,
          userName: credentials.userName,
        });
        await engine.startLocalStream(localViewRef.current);
        if (micMutedRef.current) engine.setMicrophoneMuted(true);
        if (cameraOffRef.current) engine.setCameraOff(true);

        if (!mountedRef.current) {
          await engine.teardown();
          return;
        }
        setSession(activeSession);
        apply(hasRemoteStreamRef.current ? "ongoing" : "waitingForPatient");
      } catch (err) {
        await engineRef.current?.teardown();
        engineRef.current = null;
        if (!mountedRef.current) return;
        setError(toCallError(err));
        apply("error");
      } finally {
        connectingRef.current = false;
      }
    },
    [apply, attachRemote, bookingId, markPatientAttended],
  );

  // Keep the callback-facing refs in sync with rendered state.
  useEffect(() => {
    hasRemoteStreamRef.current = hasRemoteStream;
  }, [hasRemoteStream]);
  useEffect(() => {
    micMutedRef.current = micMuted;
  }, [micMuted]);
  useEffect(() => {
    cameraOffRef.current = cameraOff;
  }, [cameraOff]);

  /** Mount / refresh recovery: GET the session BEFORE anything else. */
  const recover = useCallback(async () => {
    apply("checking");
    setError(null);
    try {
      const { session: active } = await videoCallService.getCallSession(bookingId);
      setSession(active);
      // A session already exists, so this is a rejoin — the room, the patient
      // and the backend CallSession all survived the doctor navigating away.
      setResuming(true);
      await connectSdk(active);
    } catch (err) {
      if (!mountedRef.current) return;
      if (isNoActiveSession(err)) {
        setSession(null);
        apply("idle");
        return;
      }
      setError(toCallError(err));
      apply("error");
    }
  }, [apply, bookingId, connectSdk]);

  useEffect(() => {
    mountedRef.current = true;
    void recover();
    return () => {
      mountedRef.current = false;
      void engineRef.current?.teardown();
      engineRef.current = null;
      setLocalStream(null);
    };
  }, [recover]);

  const startCall = useCallback(() => {
    void (async () => {
      apply("initiating");
      setError(null);
      setResuming(false);
      try {
        const { session: created } = await videoCallService.initiateCall(bookingId);
        setSession(created);
        await connectSdk(created);
      } catch (err) {
        // 409 = a session already exists; recover it instead of failing.
        if (err instanceof ApiError && err.status === 409) {
          await recover();
          return;
        }
        if (!mountedRef.current) return;
        setError(toCallError(err));
        apply("error");
      }
    })();
  }, [apply, bookingId, connectSdk, recover]);

  const retry = useCallback(() => {
    void (async () => {
      await engineRef.current?.teardown();
      engineRef.current = null;
      setHasRemoteStream(false);
      setRemoteUserPresent(false);
      await recover();
    })();
  }, [recover]);

  // The SDK call is made here rather than inside the state updater: React
  // invokes updaters twice under StrictMode, which fired every mute at the SDK
  // twice (visible as doubled publish.audioMute records in the log).
  const toggleMic = useCallback(() => {
    const next = !micMutedRef.current;
    micMutedRef.current = next;
    engineRef.current?.setMicrophoneMuted(next);
    setMicMuted(next);
  }, []);

  const toggleCamera = useCallback(() => {
    const next = !cameraOffRef.current;
    cameraOffRef.current = next;
    engineRef.current?.setCameraOff(next);
    setCameraOff(next);
  }, []);

  const endCall = useCallback(() => {
    if (stateRef.current === "ending" || stateRef.current === "ended") return;
    void (async () => {
      apply("ending");
      let duration: number | null = null;
      try {
        const { session: ended } = await videoCallService.endCall(bookingId);
        duration = ended.duration;
        setSession(ended);
        setEndedDuration(ended.duration);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setEndedDuration(null);
        } else {
          if (mountedRef.current) {
            setError(toCallError(err));
            apply("error");
          }
          return;
        }
      }
      await engineRef.current?.teardown();
      engineRef.current = null;

      log.info("call", "ended", {
        bookingId,
        durationSeconds: duration,
        patientAttended: patientAttendedRef.current,
      });
      void queryClient.invalidateQueries({ queryKey: doctorKeys.bookings });
      void queryClient.invalidateQueries({ queryKey: doctorKeys.stats });

      if (!mountedRef.current) return;
      setHasRemoteStream(false);
      setRemoteUserPresent(false);
      setLocalStream(null);
      apply("ended");
    })();
  }, [apply, bookingId, queryClient]);

  return {
    state,
    session,
    error,
    micMuted,
    cameraOff,
    remoteUserPresent,
    hasRemoteStream,
    endedDuration,
    patientAttended,
    resuming,
    localStream,
    setLocalView,
    remoteVideoRef,
    startCall,
    retry,
    toggleMic,
    toggleCamera,
    endCall,
  };
}
